import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  Input,
  Popup,
  Table,
  TableRow,
  TextArea,
  TopBar,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BusinessArea,
  Company,
  DeliveryNote,
  DeliveryNoteCondition,
  DeliveryNoteMailConfiguration,
  DeliveryType,
  Language,
  MailType,
  Offer,
  Office,
  Order,
  Project,
  Task,
  UnitCode,
  createAndSaveDeliveryNotePdf,
  createAndSendDeliveryNote,
  createTransientDeliveryNotePdf,
  generateDropdownOptions,
  generateDropdownOptionsForLanguage,
  generateNotification,
  getBusinessAreaById,
  getCompanyById,
  getCustomerById,
  getOfferById,
  getOfficeById,
  getOrderById,
  saveNewOrUpdatedDeliveryNote,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { getProjectById } from 'shared/src/project/Project.axios';
import { uid } from 'uid';
import { MailPopup } from '../../components/mailPopup/MailPopup';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { useUser } from '../../components/UserContext';
import { generateEmptyCustomAddress } from '../../utils/customer/Customer.utils';
import { convertTasksIntoDeliveryNoteTableEntries } from '../../utils/deliveryNotes/DeliveryNotes.utils';
import { generateEmptyMailConfiguration } from '../../utils/mail/Mail.utils';
import { generateEmptyDeliveryNote } from '../../utils/order/Order.utils';
import { combineStandardAndCustomerTasks, generateEmptyTask } from '../../utils/task/task.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { preventSubmitOnEnter } from '../../utils/general.utils';

export const DeliveryNoteCreate: React.FC = () => {
  const { axios, user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation<{ project: Project; note: DeliveryNote }>();
  const [order, setOrder] = useState<Order>();
  const [project, setProject] = useState<Project | undefined>(location.state?.project);
  const formRef = useRef<HTMLFormElement>(null);
  const [businessArea, setBusinessArea] = useState<BusinessArea>();
  const [company, setCompany] = useState<Company>();
  const [offer, setOffer] = useState<Offer>();
  //If note is provided use it, kse generate new note for project
  const [deliveryNote, setDeliveryNote] = useState<DeliveryNote>(
    location.state?.note
      ? location.state.note
      : generateEmptyDeliveryNote({
          orderId: location.state?.project?.orderId,
          projectId: location.state?.project?.id,
        }),
  );
  const [showMailPopup, toggleMailPopup] = useState<boolean>(false);

  const [office, setOffice] = useState<Office>();
  const [documentToShow, setDocumentToShow] = useState<Blob>();

  const [mailConfig, setMailConfig] = useState<DeliveryNoteMailConfiguration>({
    ...generateEmptyMailConfiguration(),
    type: MailType.DELIVERY_NOTE,
    activeUser: user.id,
    deliveryNote,
  });

  const { data: customerArticles } = useData('CUSTOMER_ARTICLE_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: allTasks } = useData('TASKS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomers } = useData('CUSTOMER_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerUsers } = useData('CUSTOMER_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  //Load project if note is connected to it
  useEffect(() => {
    if (!project && deliveryNote.projectId)
      getProjectById(axios, deliveryNote.projectId).then(setProject);
  }, [axios, deliveryNote.projectId, project]);

  //Loads all neccessary data to be displayed
  useEffect(() => {
    if (!order || !project) return;
    Promise.all([
      getOfficeById(axios, order.officeId),
      getBusinessAreaById(axios, order.businessAreaId),
      getCompanyById(axios, order.companyId),
      getCustomerById(axios, order.customerId),
      getOfferById(axios, project.acceptedOfferId),
    ]).then(([loadedOffice, loadedBusinessArea, loadedCompany, loadedCustomer, loadedOffer]) => {
      setOffice(loadedOffice);
      setBusinessArea(loadedBusinessArea);
      setCompany(loadedCompany);
      setOffer(loadedOffer);
      setDeliveryNote({
        ...deliveryNote,
        language: loadedOffer?.language || user.language || Language.DE,
        areaId: order.businessAreaId,
      });
      loadedOffer &&
        loadedCustomer &&
        setMailConfig({
          ...mailConfig,
          receiver: loadedCustomer.contact.mail,
          language: loadedOffer.language,
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // hook to update the mailConfig for customNotes
  useEffect(() => {
    if (!deliveryNote.customConfig) return;
    setMailConfig((prev) => ({
      ...prev,
      receiver:
        loadedCustomerUsers.find(
          (customer) => customer.id === deliveryNote.customConfig?.customerContactId,
        )?.contact.mail || '',
      language: user.language || Language.DE,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryNote.customConfig,
    deliveryNote.customConfig?.customerContactId,
    loadedCustomerUsers,
  ]);

  //Hook to load the order for the selected project
  useEffect(() => {
    if (!project?.id) return;
    getOrderById(axios, project.orderId).then((loadedOrder) => {
      setDeliveryNote({
        ...deliveryNote,
        mailRecipientId: loadedOrder?.deliveryNoteRecipientId,
        customerOrderNumber: !!deliveryNote.customerOrderNumber
          ? deliveryNote.customerOrderNumber
          : loadedOrder?.customerOrderNumber,
      });
      setOrder(loadedOrder);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  /**
   * Submit handler to create the new deliveryNote and show the preview
   * @param evt FormEvent to prevent default behaviour
   */
  const showPreview = async (): Promise<void> => {
    return saveNewOrUpdatedDeliveryNote(axios, deliveryNote).then((generatedNote) => {
      if (!generatedNote) return;
      setDeliveryNote(generatedNote);
      createTransientDeliveryNotePdf(axios, {
        deliveryNote: generatedNote,
        language: deliveryNote.language || Language.DE,
        user,
      }).then((generatedDocument) => generatedDocument && setDocumentToShow(generatedDocument));
    });
  };

  /**
   * Holds the tasks and the customer specific articles as tasks
   */
  const availableTasks: Task[] = useMemo((): Task[] => {
    const customerId: string | undefined = deliveryNote.customConfig?.customerId
      ? deliveryNote.customConfig.customerId
      : order?.customerId;
    const officeId: string | undefined = deliveryNote.customConfig?.officeId
      ? deliveryNote.customConfig.customerId
      : order?.officeId;
    return combineStandardAndCustomerTasks(allTasks, customerArticles, customerId, officeId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allTasks,
    customerArticles,
    deliveryNote.customConfig?.customerId,
    offer?.language,
    order?.customerId,
  ]);
  /**
   * Offered tasks in an editable table
   */
  const rows: TableRow[] = useMemo(
    (): TableRow[] =>
      convertTasksIntoDeliveryNoteTableEntries(
        deliveryNote,
        availableTasks,
        setDeliveryNote,
        (id: string) =>
          setDeliveryNote({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.filter(
              (position) => position.id !== id,
            ),
          }),
        user.language || Language.DE,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveryNote, availableTasks],
  );

  /**
   * Helper method to create a deliveryNotePdf and upload it to the projects documents
   * @param sendMail decides if the mail should be sent or not
   */
  const handleCreateAndSavePdf = async (
    redirect: boolean,
    sendMail: boolean = false,
  ): Promise<void | boolean> => {
    if (!sendMail)
      return createAndSaveDeliveryNotePdf(axios, {
        deliveryNote: deliveryNote,
        language: offer?.language || Language.DE,
        user,
      }).then((savedNote) => {
        if (redirect) navigate(-1);
        if (savedNote) setDeliveryNote(savedNote);
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.saveDeliveryNote'),
        });
      });
    else
      return createAndSendDeliveryNote(axios, { ...mailConfig, deliveryNote }).then((success) => {
        if (success) {
          toggleMailPopup(false);
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.sendDeliveryNote'),
          });
        }
      });
  };

  /**
   * Holds the headerInfo, which is different for project generated notes and manually created notes
   */
  const headerInfo: React.JSX.Element = useMemo((): React.JSX.Element => {
    if (deliveryNote.projectId)
      return (
        <>
          <Input
            type="text"
            disabled
            value={project?.numberRangeNumber}
            label={t('pages.deliveryNote.create.projectNumber')}
          />
          <Input
            type="text"
            disabled
            value={deliveryNote?.numberRangeNumber}
            label={t('pages.deliveryNote.create.deliveryNumber')}
          />
          <div className="three-columns">
            <Input
              type="text"
              disabled
              value={office?.name}
              label={t('pages.deliveryNote.create.office')}
            />
            <Input
              type="text"
              disabled
              value={businessArea?.name}
              label={t('pages.deliveryNote.create.businessArea')}
            />
            <Input
              type="text"
              disabled
              value={company?.name}
              label={t('pages.deliveryNote.create.company')}
            />
          </div>
        </>
      );
    else {
      return (
        <>
          <div className="three-columns">
            <Dropdown
              selectedOption={deliveryNote.customConfig?.officeId}
              options={generateDropdownOptions(loadedOffices, 'name', 'id')}
              label={t('pages.deliveryNote.create.office')}
              disabled
              onChange={() => {}}
            />
            <Input
              type="text"
              disabled
              value={deliveryNote.numberRangeNumber}
              label={t('pages.deliveryNote.create.deliveryNumber')}
            />
          </div>
          <div className="three-columns">
            <Dropdown
              selectedOption={deliveryNote.customConfig?.customerId}
              options={generateDropdownOptions(loadedCustomers, 'name', 'id')}
              label={t('pages.deliveryNote.create.customer')}
              disabled
              onChange={() => {}}
            />
            <Dropdown
              selectedOption={deliveryNote.customConfig?.customerLocationId}
              options={generateDropdownOptions(
                loadedCustomerLocations.filter(
                  (location) =>
                    location.customerId === deliveryNote.customConfig?.customerId ||
                    location.customerId === order?.customerId,
                ),
                'name',
                'id',
              )}
              label={t('pages.deliveryNote.create.customerLocations')}
              disabled
              onChange={() => {}}
            />
            <Dropdown
              selectedOption={deliveryNote.customConfig?.customerContactId}
              options={generateDropdownOptions(
                loadedCustomerUsers,
                ['lastName', 'firstName'],
                'id',
              )}
              label={t('pages.deliveryNote.create.customerContact')}
              disabled
              onChange={() => {}}
            />
          </div>
        </>
      );
    }
  }, [
    businessArea?.name,
    company?.name,
    deliveryNote,
    loadedCustomerLocations,
    loadedCustomerUsers,
    loadedCustomers,
    loadedOffices,
    office?.name,
    order?.customerId,
    project?.numberRangeNumber,
    t,
  ]);

  return (
    <form ref={formRef} onSubmit={(evt) => evt.preventDefault()} onKeyDown={preventSubmitOnEnter}>
      <MailPopup
        referenceId={deliveryNote.id}
        userId={user.id}
        isOpen={showMailPopup}
        mailConfig={mailConfig}
        sendMail={() => handleCreateAndSavePdf(false, true)}
        toggleOpen={toggleMailPopup}
        updateConfig={(updatedConfig) => setMailConfig((old) => ({ ...old, ...updatedConfig }))}
      />
      <TopBar
        onBackClick={() => navigate(-1)}
        title={t('pages.deliveryNote.create.topBarHeadline')}
      >
        <SaveButtons handleSubmit={handleCreateAndSavePdf} />
        {deliveryNote.delivered ? (
          <Button
            value={t('general.buttons.notDelivered')}
            onClick={() => setDeliveryNote({ ...deliveryNote, delivered: false })}
          />
        ) : (
          <Button
            value={t('general.buttons.delivered')}
            onClick={() => setDeliveryNote({ ...deliveryNote, delivered: true })}
          />
        )}
        <Button
          type="button"
          onClick={() => {
            saveNewOrUpdatedDeliveryNote(axios, deliveryNote).then((savedDeliveryNote) => {
              if (!savedDeliveryNote) return;
              setDeliveryNote(savedDeliveryNote);
              toggleMailPopup(true);
            });
          }}
          value={t('general.buttons.sendMail')}
        />
        <Button type="button" value={t('general.buttons.preview')} onClick={showPreview} />
      </TopBar>

      {documentToShow && (
        <Popup isOpen={!!documentToShow} onClose={() => setDocumentToShow(undefined)}>
          <object data={URL.createObjectURL(documentToShow)} type={documentToShow.type} />
        </Popup>
      )}

      <Box title={t('pages.deliveryNote.create.basicInformation')}>
        {headerInfo}
        <div className="three-columns">
          <Dropdown
            label={t('pages.deliveryNote.create.mailRecipient')}
            onChange={(mailRecipientId) => {
              setDeliveryNote({ ...deliveryNote, mailRecipientId });
              setMailConfig({
                ...mailConfig,
                receiver:
                  loadedCustomerUsers.find((user) => user.id === mailRecipientId)?.contact.mail ||
                  '-',
              });
            }}
            options={generateDropdownOptions(
              loadedCustomerUsers.filter(
                (user) =>
                  user.customerId === deliveryNote.customConfig?.customerId ||
                  user.customerId === order?.customerId,
              ),
              ['lastName', 'firstName'],
              'id',
            )}
          />
          <Dropdown
            label={t('pages.deliveryNote.create.language')}
            options={generateDropdownOptionsForLanguage()}
            onChange={(language) =>
              setDeliveryNote({
                ...deliveryNote,
                language: language as Language,
              })
            }
            selectedOption={deliveryNote.language}
          />
          <Input
            label={t('pages.deliveryNote.create.customerOrderNumber')}
            type="text"
            value={deliveryNote.customerOrderNumber}
            onChange={(customerOrderNumber) =>
              setDeliveryNote({ ...deliveryNote, customerOrderNumber })
            }
          />
        </div>
        <Checkbox
          isChecked={!!deliveryNote.deliveryAddress}
          label={t('pages.deliveryNote.create.customAddress')}
          onCheck={(checked) =>
            setDeliveryNote({
              ...deliveryNote,
              deliveryAddress: checked ? generateEmptyCustomAddress() : undefined,
            })
          }
        />
        {deliveryNote.deliveryAddress && (
          <>
            <div className="delivery-note__custom-address__wrapper">
              <Input
                type="text"
                value={deliveryNote.deliveryAddress.name}
                label={t('pages.deliveryNote.create.name')}
                onChange={(name) =>
                  deliveryNote?.deliveryAddress &&
                  setDeliveryNote({
                    ...deliveryNote,
                    deliveryAddress: { ...deliveryNote.deliveryAddress, name },
                  })
                }
              />
              <Input
                type="text"
                value={deliveryNote.deliveryAddress.name2}
                label={t('pages.deliveryNote.create.name2')}
                onChange={(name2) =>
                  deliveryNote?.deliveryAddress &&
                  setDeliveryNote({
                    ...deliveryNote,
                    deliveryAddress: { ...deliveryNote.deliveryAddress, name2 },
                  })
                }
              />
              <div className="delivery-note__custom-address__wrapper__street">
                <Input
                  maxWidth={'95%'}
                  type="text"
                  value={deliveryNote.deliveryAddress.street}
                  label={t('pages.deliveryNote.create.street')}
                  onChange={(street) =>
                    deliveryNote?.deliveryAddress &&
                    setDeliveryNote({
                      ...deliveryNote,
                      deliveryAddress: {
                        ...deliveryNote.deliveryAddress,
                        street,
                      },
                    })
                  }
                />
                <Input
                  type="text"
                  value={deliveryNote.deliveryAddress.streetNumber}
                  label={t('pages.deliveryNote.create.streetNumber')}
                  onChange={(streetNumber) =>
                    deliveryNote?.deliveryAddress &&
                    setDeliveryNote({
                      ...deliveryNote,
                      deliveryAddress: {
                        ...deliveryNote.deliveryAddress,
                        streetNumber,
                      },
                    })
                  }
                />
              </div>
              <div className="delivery-note__custom-address__wrapper__city">
                <Input
                  maxWidth={'95%'}
                  type="text"
                  value={deliveryNote.deliveryAddress.countryCode}
                  label={t('pages.deliveryNote.create.countryCode')}
                  onChange={(countryCode) =>
                    deliveryNote?.deliveryAddress &&
                    setDeliveryNote({
                      ...deliveryNote,
                      deliveryAddress: {
                        ...deliveryNote.deliveryAddress,
                        countryCode,
                      },
                    })
                  }
                />
                <Input
                  maxWidth={'95%'}
                  type="text"
                  value={deliveryNote.deliveryAddress.zip}
                  label={t('pages.deliveryNote.create.zip')}
                  onChange={(zip) =>
                    deliveryNote?.deliveryAddress &&
                    setDeliveryNote({
                      ...deliveryNote,
                      deliveryAddress: { ...deliveryNote.deliveryAddress, zip },
                    })
                  }
                />
                <Input
                  type="text"
                  value={deliveryNote.deliveryAddress.city}
                  label={t('pages.deliveryNote.create.city')}
                  onChange={(city) =>
                    deliveryNote?.deliveryAddress &&
                    setDeliveryNote({
                      ...deliveryNote,
                      deliveryAddress: {
                        ...deliveryNote.deliveryAddress,
                        city,
                      },
                    })
                  }
                />
              </div>
            </div>
          </>
        )}
      </Box>
      <Box title={t('pages.deliveryNote.create.deliveryNote')}>
        <div className="three-columns">
          <Input
            type="date"
            onChangeDate={(deliveryDate) =>
              deliveryDate && setDeliveryNote({ ...deliveryNote, deliveryDate })
            }
            value={deliveryNote.deliveryDate}
            label={t('pages.deliveryNote.create.deliveryDate')}
          />
          <Input
            type="date"
            onChangeDate={(startDate) =>
              startDate &&
              setDeliveryNote({
                ...deliveryNote,
                performancePeriod: {
                  ...deliveryNote.performancePeriod,
                  startDate,
                },
              })
            }
            value={deliveryNote.performancePeriod.startDate}
            label={t('pages.deliveryNote.create.periodStart')}
          />
          <Input
            type="date"
            onChangeDate={(endDate) =>
              endDate &&
              setDeliveryNote({
                ...deliveryNote,
                performancePeriod: {
                  ...deliveryNote.performancePeriod,
                  endDate,
                },
              })
            }
            value={deliveryNote.performancePeriod.endDate}
            label={t('pages.deliveryNote.create.periodEnd')}
          />
        </div>
        <TextArea
          value={deliveryNote.annotation}
          onChange={(annotation) => setDeliveryNote({ ...deliveryNote, annotation })}
          label={t('pages.deliveryNote.create.annotation')}
        />
        <IconComponent
          type={IconType.ADD}
          className="delivery-note__plus-icon"
          width={30}
          onClick={() =>
            setDeliveryNote({
              ...deliveryNote,
              deliveredPositions: [
                ...deliveryNote.deliveredPositions,
                {
                  batchNumber: '',
                  comment: '',
                  condition: DeliveryNoteCondition.IO,
                  id: uid(),
                  task: generateEmptyTask(),
                  unitCode: UnitCode.NUMBER_OF_PARTS,
                  position: deliveryNote.deliveredPositions.length + 1,
                  deliveryType: DeliveryType.FULL,
                },
              ],
            })
          }
        />
        <Table
          rows={rows}
          header={
            t('pages.deliveryNote.create.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </form>
  );
};
